import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, KeyRound, ArrowLeft, CheckCircle, Mail } from 'lucide-react';
import { requestResetPassword, confirmResetPassword } from '@/lib/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // request, sent, reset, success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setLoading(true);
    try {
      await requestResetPassword(email);
      setStep('sent');
      toast.success('Email inviata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setLoading(true);
    try {
      await confirmResetPassword(token, password);
      setStep('success');
      toast.success('Password aggiornata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
            <KeyRound className="h-6 w-6 text-sky-600" />
          </div>
          <CardTitle style={{ fontFamily: 'Playfair Display, serif' }}>
            {step === 'request' && 'Recupera Password'}
            {step === 'sent' && 'Email Inviata'}
            {step === 'reset' && 'Nuova Password'}
            {step === 'success' && 'Password Aggiornata'}
          </CardTitle>
          <CardDescription>
            {step === 'request' && 'Inserisci la tua email per ricevere il link di reset'}
            {step === 'sent' && 'Controlla la tua casella email'}
            {step === 'reset' && 'Inserisci la tua nuova password'}
            {step === 'success' && 'La tua password è stata aggiornata con successo'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Step: Richiesta email */}
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="La tua email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Invia Link di Reset
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-sky-600 hover:underline">
                  <ArrowLeft className="h-3 w-3 inline mr-1" />
                  Torna al Login
                </Link>
              </div>
            </form>
          )}
          
          {/* Step: Email inviata */}
          {step === 'sent' && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-slate-600">
                Se l'email <strong>{email}</strong> è registrata, riceverai un link per reimpostare la password.
              </p>
              <p className="text-sm text-slate-500">
                Controlla anche la cartella spam.
              </p>
              <Button 
                variant="outline" 
                onClick={() => { setStep('request'); setEmail(''); }}
                className="mt-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Indietro
              </Button>
            </div>
          )}
          
          {/* Step: Reset password */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="password">Nuova Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimo 6 caratteri"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Ripeti la password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Aggiorna Password
              </Button>
            </form>
          )}
          
          {/* Step: Successo */}
          {step === 'success' && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-slate-600">
                Ora puoi accedere con la tua nuova password.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full bg-slate-900 hover:bg-slate-800">
                Vai al Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
